import { Button, Spinner } from "@chakra-ui/react";
import React from "react";
import Card from "components/card";
import {
  emissionProfilesApi,
  organizationCronJobsApi,
  organizationsApi,
} from "api/endpoints";
import { useShallow } from "zustand/react/shallow";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";

const JOB_LABELS = {
  libro_ventas_diario_fill: "Ventas — borrador diario (23:59)",
  libro_compras_diario_fill: "Compras — borrador diario (23:59)",
  libro_boletas_diario_fill: "Boletas — borrador diario (23:59)",
  libro_guias_diario_fill: "Guías — borrador diario (23:59)",
  libro_ventas_mensual: "Ventas — cierre mensual (último día 23:59)",
  libro_compras_mensual: "Compras — cierre mensual (último día 23:59)",
  libro_boletas_mensual: "Boletas — cierre mensual (último día 23:59)",
  libro_guias_mensual: "Guías — cierre mensual (último día 23:59)",
};

function labelForJobType(t) {
  return JOB_LABELS[t] || t;
}

export default function DteLibrosCronJobsPanel() {
  const emission = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      ambiente: s.ambiente,
    })),
  );
  const selectedAmbiente =
    emission.kind === "ready" && emission.ambiente ? emission.ambiente : "";
  const [orgs, setOrgs] = React.useState([]);
  const [profiles, setProfiles] = React.useState([]);
  const [loadMeta, setLoadMeta] = React.useState({ loading: true, error: "" });

  const [orgUuid, setOrgUuid] = React.useState("");
  const [rutEmisor, setRutEmisor] = React.useState("");
  const [emissionProfileInternalId, setEmissionProfileInternalId] =
    React.useState("");

  const [existingJobs, setExistingJobs] = React.useState([]);
  const [listLoading, setListLoading] = React.useState(false);
  const [listError, setListError] = React.useState("");

  const [actionLoading, setActionLoading] = React.useState(false);
  const [message, setMessage] = React.useState({ type: "", text: "" });

  React.useEffect(() => {
    if (!selectedAmbiente) {
      setOrgs([]);
      setProfiles([]);
      setLoadMeta({ loading: false, error: "" });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadMeta({ loading: true, error: "" });
      try {
        const [oRes, pRes] = await Promise.all([
          organizationsApi.list(),
          emissionProfilesApi.list({ ambiente: selectedAmbiente }),
        ]);
        if (cancelled) return;
        if (!oRes.res?.ok) {
          const msg =
            oRes.data?.error ||
            oRes.data?.message ||
            `Organizaciones: error ${oRes.res.status}`;
          setLoadMeta({ loading: false, error: msg });
          return;
        }
        if (!pRes.res?.ok) {
          const msg =
            pRes.data?.error ||
            pRes.data?.message ||
            `Perfiles: error ${pRes.res.status}`;
          setLoadMeta({ loading: false, error: msg });
          return;
        }
        const oItems = Array.isArray(oRes.data?.items) ? oRes.data.items : [];
        const pItems = Array.isArray(pRes.data?.items) ? pRes.data.items : [];
        setOrgs(oItems);
        setProfiles(pItems);
        if (oItems.length > 0) {
          setOrgUuid(String(oItems[0].organization_uuid || ""));
        }
      } catch (e) {
        if (!cancelled) {
          setLoadMeta({
            loading: false,
            error: e instanceof Error ? e.message : "Error al cargar datos.",
          });
        }
        return;
      }
      if (!cancelled) {
        setLoadMeta({ loading: false, error: "" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedAmbiente]);

  const refreshJobList = React.useCallback(async (uuid) => {
    if (!uuid) {
      setExistingJobs([]);
      setListError("");
      return;
    }
    setListLoading(true);
    setListError("");
    try {
      const { res, data } = await organizationCronJobsApi.list(uuid);
      if (!res.ok) {
        setExistingJobs([]);
        setListError(
          data?.error ||
            data?.message ||
            `No se pudo cargar la lista (${res.status}).`,
        );
        return;
      }
      if (data && data.ok === false) {
        setExistingJobs([]);
        setListError(
          data.error || data.message || "Error al listar cron jobs.",
        );
        return;
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      setExistingJobs(items);
    } catch (e) {
      setExistingJobs([]);
      setListError(
        e instanceof Error ? e.message : "Error de red al cargar cron jobs.",
      );
    } finally {
      setListLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!orgUuid) {
      setExistingJobs([]);
      setListError("");
      return;
    }
    refreshJobList(orgUuid);
  }, [orgUuid, refreshJobList]);

  const selectedOrg = React.useMemo(
    () => orgs.find((o) => String(o.organization_uuid) === String(orgUuid)),
    [orgs, orgUuid],
  );

  const profilesForOrg = React.useMemo(() => {
    if (!selectedOrg?.id) return [];
    const oid = Number(selectedOrg.id);
    return profiles.filter(
      (p) =>
        p.organization_id != null && Number(p.organization_id) === oid,
    );
  }, [profiles, selectedOrg]);

  React.useEffect(() => {
    if (!orgUuid) return;
    if (profilesForOrg.length === 0) {
      setEmissionProfileInternalId("");
      setRutEmisor("");
      return;
    }
    if (profilesForOrg.length === 1) {
      const only = profilesForOrg[0];
      const onlyId = String(only.internal_id ?? "");
      if (String(emissionProfileInternalId) !== onlyId) {
        setEmissionProfileInternalId(onlyId);
        setRutEmisor(String(only.rut_emisor ?? "").trim());
      }
      return;
    }
    const valid = profilesForOrg.some(
      (x) => String(x.internal_id) === String(emissionProfileInternalId),
    );
    if (emissionProfileInternalId && !valid) {
      setEmissionProfileInternalId("");
      setRutEmisor("");
    }
  }, [orgUuid, profilesForOrg, emissionProfileInternalId]);

  const onProfileChange = React.useCallback((internalId) => {
    setEmissionProfileInternalId(internalId);
    const p = profilesForOrg.find((x) => String(x.internal_id) === internalId);
    if (p?.rut_emisor) {
      setRutEmisor(String(p.rut_emisor).trim());
    }
  }, [profilesForOrg]);

  const handleBootstrap = React.useCallback(async () => {
    setMessage({ type: "", text: "" });
    const uuid = String(orgUuid || "").trim();
    const rut = String(rutEmisor || "").trim();
    const profId = String(emissionProfileInternalId || "").trim();
    if (!uuid) {
      setMessage({ type: "error", text: "Elegí una organización." });
      return;
    }
    if (!rut) {
      setMessage({ type: "error", text: "El RUT emisor es obligatorio." });
      return;
    }
    if (!profId) {
      setMessage({
        type: "error",
        text: "Indicá el perfil de emisión (internal ID) para los libros.",
      });
      return;
    }

    setActionLoading(true);
    try {
      const { res, data } = await organizationCronJobsApi.bootstrap(uuid, {
        rut_emisor: rut,
        estado: "activo",
        payload: {
          emissionProfileInternalId: profId,
        },
      });
      if (!res.ok || !data?.ok) {
        setMessage({
          type: "error",
          text:
            data?.error ||
            data?.message ||
            `No se pudo activar (${res.status}).`,
        });
        return;
      }
      const n = Number(data.total ?? data.items?.length ?? 0);
      setMessage({
        type: "success",
        text: `Listo: quedaron ${n} cron jobs configurados para este emisor (activos). El servidor debe tener CRON_JOBS_WORKER_ENABLED=true para ejecutarlos.`,
      });
      await refreshJobList(uuid);
    } catch (e) {
      setMessage({
        type: "error",
        text: e instanceof Error ? e.message : "Error de red.",
      });
    } finally {
      setActionLoading(false);
    }
  }, [
    orgUuid,
    rutEmisor,
    emissionProfileInternalId,
    refreshJobList,
  ]);

  if (loadMeta.loading) {
    return (
      <Card extra="w-full p-8 flex flex-col items-center gap-3">
        <Spinner color="brand.500" size="lg" thickness="4px" />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Cargando organizaciones y perfiles…
        </p>
      </Card>
    );
  }

  if (loadMeta.error) {
    return (
      <Card extra="w-full p-6">
        <p className="text-sm text-red-600 dark:text-red-300" role="alert">
          {loadMeta.error}
        </p>
      </Card>
    );
  }

  if (!orgs.length) {
    return (
      <Card extra="w-full p-6">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          No hay organizaciones en tu cuenta. Creá una organización antes de configurar
          cron jobs.
        </p>
      </Card>
    );
  }

  return (
    <Card extra="w-full p-4 sm:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Cron jobs de libros SII
        </h4>
        <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
          Creá o actualizá los <strong>8 jobs programados</strong> (4 borradores diarios +
          4 cierres mensuales) para un RUT emisor y perfil de emisión. Es el mismo flujo que{" "}
          <code className="rounded bg-gray-100 px-1 text-xs dark:bg-white/10">
            POST …/cron-jobs/bootstrap
          </code>
          .
        </p>
      </div>

      <div className="rounded-xl border border-amber-300/50 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/35 dark:text-amber-100">
        En el <strong>servidor</strong> debe estar{" "}
        <code className="rounded bg-white/80 px-1 text-xs dark:bg-navy-900">
          CRON_JOBS_WORKER_ENABLED=true
        </code>{" "}
        para que el backend ejecute estos jobs desde la tabla{" "}
        <code className="text-xs">cron_jobs</code>.
      </div>

      {profiles.length === 0 ? (
        <div
          className="mt-4 rounded-xl border border-amber-300/50 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/35 dark:text-amber-100"
          role="status"
        >
          No hay perfiles de emisión en el sistema. Los <strong>jobs ya creados</strong> se
          listan abajo; para <strong>activar o actualizar</strong> cron jobs desde acá
          necesitás al menos un perfil de emisión.
        </div>
      ) : null}

      {orgUuid &&
      selectedOrg &&
      profiles.length > 0 &&
      profilesForOrg.length === 0 ? (
        <div
          className="mt-4 rounded-xl border border-rose-300/50 bg-rose-50/90 px-4 py-3 text-sm text-rose-950 dark:border-rose-500/30 dark:bg-rose-950/35 dark:text-rose-100"
          role="alert"
        >
          No hay perfiles de emisión <strong>vinculados</strong> a esta organización en la
          base de datos (<code className="text-xs">dte_emission_profiles.organization_id</code>
          ). El cron valida que el RUT pertenezca a la org del URL; actualizá el perfil o
          asigná <code className="text-xs">organization_id</code> al registro correcto.
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {orgs.length > 1 ? (
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
            Organización
            <select
              value={orgUuid}
              onChange={(e) => setOrgUuid(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Seleccionar…</option>
              {orgs.map((o) => (
                <option
                  key={o.organization_uuid}
                  value={o.organization_uuid}
                >
                  {o.name || o.internal_id || o.organization_uuid}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <input type="hidden" readOnly value={orgUuid} aria-hidden />
        )}

        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
          Perfil de emisión (solo los de esta organización)
          <select
            value={emissionProfileInternalId}
            onChange={(e) => onProfileChange(e.target.value)}
            disabled={!orgUuid || profilesForOrg.length === 0}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/15 dark:bg-navy-800 dark:text-white"
          >
            <option value="">
              {profilesForOrg.length === 0
                ? "Sin perfiles vinculados a esta org…"
                : "Seleccionar perfil…"}
            </option>
            {profilesForOrg.map((p) => (
              <option key={p.id ?? p.internal_id} value={p.internal_id}>
                {p.profile_name || p.internal_id} · {p.rut_emisor} ({p.ambiente})
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300 lg:col-span-2">
          RUT emisor (debe coincidir con el perfil y estar asociado a la organización)
          <input
            type="text"
            value={rutEmisor}
            onChange={(e) => setRutEmisor(e.target.value)}
            placeholder="12345678-9"
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
          />
        </label>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button
          colorScheme="brand"
          size="md"
          isLoading={actionLoading}
          loadingText="Activando…"
          onClick={handleBootstrap}
          isDisabled={
            !orgUuid ||
            actionLoading ||
            profilesForOrg.length === 0 ||
            !String(emissionProfileInternalId || "").trim()
          }
        >
          Activar cron jobs de libros
        </Button>
        <Button
          variant="outline"
          size="md"
          onClick={() => refreshJobList(orgUuid)}
          isDisabled={!orgUuid || listLoading}
        >
          {listLoading ? "Actualizando lista…" : "Refrescar lista"}
        </Button>
      </div>

      {message.text ? (
        <p
          className={`mt-4 rounded-lg px-4 py-3 text-sm ${
            message.type === "success"
              ? "border border-emerald-400/40 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-100"
              : "border border-red-400/40 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200"
          }`}
          role={message.type === "error" ? "alert" : "status"}
        >
          {message.text}
        </p>
      ) : null}

      <div className="mt-8 border-t border-gray-200 pt-6 dark:border-white/10">
        <div className="text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
          Jobs registrados ({existingJobs.length})
        </div>
        {!orgUuid ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Elegí organización para ver la lista.
          </p>
        ) : listError ? (
          <p className="mt-2 text-sm text-red-600 dark:text-red-300" role="alert">
            {listError}
          </p>
        ) : listLoading ? (
          <p className="mt-2 text-sm text-gray-500">Cargando…</p>
        ) : existingJobs.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Todavía no hay cron jobs para esta organización. Usá el botón de arriba para
            crearlos.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-100 dark:divide-white/10">
            {existingJobs.map((job) => (
              <li
                key={job.cron_job_uuid || job.id}
                className="flex flex-col gap-0.5 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <span className="text-sm font-medium text-navy-700 dark:text-white">
                    {labelForJobType(job.job_type)}
                  </span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    ({job.job_type})
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-400">
                  {job.rut_emisor ? (
                    <span className="font-mono">{job.rut_emisor}</span>
                  ) : null}
                  <span
                    className={
                      job.estado === "activo"
                        ? "font-semibold text-emerald-700 dark:text-emerald-300"
                        : ""
                    }
                  >
                    {job.estado}
                  </span>
                  {job.next_run_at ? (
                    <span>próx.: {job.next_run_at}</span>
                  ) : null}
                  {job.last_status ? (
                    <span>último: {job.last_status}</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
